import { ReactNode, useEffect, useState } from 'react';

import cn from 'classnames';
import { useHistory } from 'react-router';

import { disableScrolling, enableScrolling } from 'components/Header/partials/get_change';
import Render from 'components/render';
import { TESTNET_URL, IS_TESTNET_LINK_IN_INDIAN_EXCHANGE } from 'constants/constants';
import { MIXPANEL_EVENT_NAMES, MIXPANEL_SOURCES } from 'constants/mixpanelEventNames';
import routes, { LANDING_PAGE_ROUTES } from 'constants/routes';
import { trackMixpanelEvent } from 'helpers/mixpanel-init';
import { navigateTo } from 'helpers/url';
import { addBaseURL } from 'helpers/utils';
import { useWindowSize } from 'hooks/useWindowSize';
import i18n from 'i18n/config';
import { getUserId } from 'selectors/otherSelectors';
import { useAppSelector } from 'storeHooks';
import {
  AngleRightIcon,
  ArrowDown,
  OptionAnalyticsIcon,
  DemoTradingIcon,
  StrategyBuilder,
} from 'styles/assets/icons';
import OffersIcon from 'styles/assets/icons/OffersIcon';
import ShareBorderIcon from 'styles/assets/icons/ShareBorderIcon';
import Box from 'UIKit/Box';
import DeltaButton from 'UIKit/DeltaButton';

import { getMixpanelPageName } from '../universalSearch/partials/helpers';
import styles from './moreMenu.module.scss';

type MoreMenuHead =
  | 'optionAnalytics'
  | 'tradeHistory'
  | 'tradingBot'
  | 'offers'
  | 'referralProgram'
  | 'strategyBuilder'
  | 'demoTrading';

type MoreMenuKey = 'label' | 'description';

interface ItemProps {
  id: number;
  label: string;
  href: string;
  description: string;
  key: string;
  contract_type?: string;
  mixpanelLabel?: string;
  icon: ReactNode;
  isExternal?: boolean;
  mixPanelAttribute?: {
    mixpanelSource?: string;
    site_demarkation?: string;
  };
}

const moreMenuDataText = i18n.t('headerFooter:header.moreMenu.data');
const moreMenuTradeText = i18n.t('headerFooter:header.moreMenu.trade');
const moreMenuText = i18n.t('headerFooter:header.more');
const rewardsAndPromotionsText = i18n.t(
  'headerFooter:header.moreMenu.rewardsAndPromotions'
);

const getTranslation = (
  str: string,
  key: string,
  values?: Record<string, string | number>
) => {
  return i18n.t(`headerFooter:header.moreMenu.${str}.${key}` as const, { ...values });
};

const MoreMenuIndian = () => {
  const [isDropdownOpen, setDropdownOpen] = useState(false);
  const { width: screenWidth } = useWindowSize();
  const userId = useAppSelector(getUserId);

  const history = useHistory();

  const getLocalizedString = (
    str: MoreMenuHead,
    key: MoreMenuKey,
    values?: Record<string, string | number>
  ): string => {
    return getTranslation(str, key, values);
  };

  useEffect(() => {
    if (isDropdownOpen) {
      disableScrolling();
    } else {
      enableScrolling();
    }
  }, [isDropdownOpen]);

  const tradeMenuItems = [
    {
      id: 1,
      label: getLocalizedString('strategyBuilder', 'label'),
      href: addBaseURL(`${routes.options_chain.trade}?activeTab=basketOrders`),
      description: getLocalizedString('strategyBuilder', 'description'),
      key: 'strategyBuilder',
      icon: <StrategyBuilder className={styles.leaderBoardIcon} />,
      mixpanelLabel: userId
        ? MIXPANEL_EVENT_NAMES.BASKET_ORDER_SELECTED
        : MIXPANEL_EVENT_NAMES.BASKET_ORDER_MORE_HEADER_SELECTED,
      mixPanelAttribute: {
        mixpanelSource: MIXPANEL_SOURCES.MORE_DROPDOWN,
        site_demarkation: MIXPANEL_SOURCES.WEB_APP,
      },
    },
    {
      id: 2,
      label: getLocalizedString('demoTrading', 'label'),
      href: TESTNET_URL,
      description: getLocalizedString('demoTrading', 'description'),
      key: 'demoTrading',
      icon: <DemoTradingIcon className={styles.leaderBoardIcon} />,
      mixpanelLabel: MIXPANEL_EVENT_NAMES.TESTNET_LINK_CLICKED,
      show: IS_TESTNET_LINK_IN_INDIAN_EXCHANGE,
      mixPanelAttribute: {
        mixpanelSource: MIXPANEL_SOURCES.MORE_DROPDOWN,
        site_demarkation: MIXPANEL_SOURCES.WEB_APP,
      },
      isExternal: true,
    },
  ];

  const resourceMenuItems = [
    {
      id: 1,
      label: getLocalizedString('offers', 'label'),
      href: LANDING_PAGE_ROUTES.offers,
      description: getLocalizedString('offers', 'description'),
      key: 'offers',
      icon: <OffersIcon className={styles.resourcesIcon} />,
      mixpanelLabel: 'Offers India - Header',
      show: screenWidth < 1440,
    },
    {
      id: 2,
      label: getLocalizedString('referralProgram', 'label'),
      href: LANDING_PAGE_ROUTES.referralProgram,
      description: getLocalizedString('referralProgram', 'description'),
      key: 'referralProgram',
      icon: <ShareBorderIcon className={styles.resourcesIcon} />,
      mixpanelLabel: 'Referral Program India - Header',
      show: true,
    },
  ];

  const dataMenuItems = [
    {
      id: 1,
      label: getLocalizedString('optionAnalytics', 'label'),
      href: routes.optionsAnalyticsDashboard.default,
      description: getLocalizedString('optionAnalytics', 'description', {
        length: 50,
      }),
      key: 'optionAnalytics',
      icon: <OptionAnalyticsIcon className={styles.leaderBoardIcon} />,
    },
  ];

  const onItemClick = (item: ItemProps) => {
    setDropdownOpen(false);
    const { mixpanelLabel, href, isExternal = false, mixPanelAttribute = {} } = item;
    const target = isExternal ? '_blank' : '_self';

    if (mixpanelLabel) {
      trackMixpanelEvent(mixpanelLabel, {
        page_name: getMixpanelPageName(),
        ...mixPanelAttribute,
      });
    }
    navigateTo(href, target);
  };

  const onDataItemClick = (item: ItemProps) => {
    setDropdownOpen(false);
    history.push(item.href);
    trackMixpanelEvent(item.mixpanelLabel, {
      page_name: getMixpanelPageName(),
      ...item.mixPanelAttribute,
    });
  };

  const isResourceHaveNoEntries = resourceMenuItems.every(({ show }) => show === false);

  return (
    <Box
      className={styles.menuContainer}
      onMouseLeave={() => setDropdownOpen(false)}
      onMouseEnter={() => setDropdownOpen(true)}
    >
      <div
        className={cn('flex-set flex--align-items-center', styles.menuHeading, {
          [styles.active]: isDropdownOpen,
        })}
      >
        {moreMenuText}
        <ArrowDown
          className={cn(styles.arrowDownIcon, {
            [styles.active]: isDropdownOpen,
          })}
        />
      </div>
      <Render when={isDropdownOpen}>
        <Box
          className={styles.dropdownMenu}
          onMouseEnter={() => setDropdownOpen(true)}
          onMouseLeave={() => setDropdownOpen(false)}
        >
          <Box renderWhen={!isResourceHaveNoEntries} className={styles.tradeSection}>
            {moreMenuTradeText}
          </Box>
          {tradeMenuItems
            .filter(({ show = true }) => show)
            .map(item => {
              return (
                <DeltaButton
                  data-testid={`tradeMenu-${item.key}`}
                  key={`tradeMenu-${item.key}`}
                  className={styles.menuBtn}
                  onClick={() => onItemClick(item)}
                  wrapperClassName={styles.menuItem}
                  useBasicButton
                >
                  {item.icon}
                  <div className={styles.menuItemText}>
                    <div className={styles.heading}>{item.label} </div>
                    <div className={styles.description}>{item.description}</div>
                  </div>
                  <AngleRightIcon className={styles.rightArrowIcon} />
                </DeltaButton>
              );
            })}
          <Box className={styles.investSection}>{moreMenuDataText}</Box>
          {dataMenuItems
            .filter(({ show = true }) => show)
            .map(item => {
              return (
                <DeltaButton
                  data-testid={`leaderboard- ${item.key}`}
                  key={`invest-${item.key}`}
                  onClick={() => onDataItemClick(item)}
                  className={styles.menuBtn}
                  wrapperClassName={styles.menuItem}
                  useBasicButton
                >
                  {item.icon}
                  <div className={styles.menuItemText}>
                    <div className={styles.heading}>{item.label}</div>
                    <div className={styles.description}>{item.description}</div>
                  </div>
                  <AngleRightIcon className={styles.rightArrowIcon} />
                </DeltaButton>
              );
            })}
          <Box renderWhen={!isResourceHaveNoEntries} className={styles.tradeSection}>
            {rewardsAndPromotionsText}
          </Box>
          {resourceMenuItems
            .filter(({ show = true }) => show)
            .map(item => {
              return (
                <DeltaButton
                  data-testid={`tradeMenu-${item.key}`}
                  key={`tradeMenu-${item.key}`}
                  className={styles.menuBtn}
                  onClick={() => onItemClick(item)}
                  wrapperClassName={styles.menuItem}
                  useBasicButton
                >
                  <div className={styles.resourceIcon}>{item.icon}</div>
                  <div className={styles.menuItemText}>
                    <div className={styles.heading}>{item.label} </div>
                    <div className={styles.description}>{item.description}</div>
                  </div>
                  <AngleRightIcon className={styles.rightArrowIcon} />
                </DeltaButton>
              );
            })}
        </Box>
      </Render>
    </Box>
  );
};
export default MoreMenuIndian;
